import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { confirm } from '@/stores/confirmStore';
import { useCompanyStore } from '../../../../stores/companyStore';
import { useQuoteEdit, QuoteEditData } from '../../../../hooks/useQuoteEdit';
import { useTaxRates } from '../../../../hooks/useTaxRates';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { isVatExempt } from '../../../../utils/vatCalculations';
import { EditableItemsTable } from '../../shared/editable/EditableItemsTable';
import { EditableClientSection } from '../../shared/editable/EditableClientSection';
import { EditableNotesSection } from '../../shared/editable/EditableNotesSection';
import { EditableDiscountSection } from '../../shared/editable/EditableDiscountSection';
import { EditActionButtons } from '../../shared/editable/EditActionButtons';
import { CompanyInfoSection } from '../../shared/CompanyInfoSection';
import { TotalsSection } from '../../shared/TotalsSection';
import { LegalFooter } from '../../shared/LegalFooter';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import { Dashboard } from '../../../Dashboard';
import { EditableQuoteExtendedFields } from '../../shared/editable/EditableQuoteExtendedFields';
import type { TemplateSettings, Company } from '../../types';

interface QuoteTemplateCorporateEditProps {
  initialData: QuoteEditData;
  template: TemplateSettings;
  company: Company | null;
  color: string;
}

/**
 * Template Agency (STARTER) - Mode édition
 * - Logo: top-right
 * - Style: ultra-minimaliste noir/blanc
 * - Table: minimal
 */
export const QuoteTemplateCorporateEdit: React.FC<QuoteTemplateCorporateEditProps> = ({
  initialData,
  template,
  company,
  color: colorProp,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company: companyStore } = useCompanyStore();
  const finalCompany = company || companyStore;

  const vatRegime = finalCompany?.vatRegime;
  const isExempt = isVatExempt(vatRegime);

  // ✅ Charger les TaxRates une seule fois au niveau du composant parent
  const { taxRates, loading: loadingTaxRates } = useTaxRates();
  const { features: companyFeatures } = useCompanyFeatures();

  const {
    quoteData,
    isSaving,
    hasChanges,
    updateField,
    updateDiscountType,
    updateDiscountValue,
    updateItem,
    addItem,
    removeItem,
    addSectionTitle,
    addComment,
    addSubtotal,
    addProductFromCatalog,
    saveQuote,
  } = useQuoteEdit(
    initialData,
    vatRegime
  );

  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const handleCancel = async () => {
    if (hasChanges) {
      const confirmed = await confirm({
        title: t('templates.common.unsavedChangesTitle'),
        message: t('templates.common.unsavedChangesMessage'),
        variant: 'warning',
        confirmText: t('templates.common.quit'),
        cancelText: t('templates.common.cancel'),
      });
      if (!confirmed) return;
    }
    navigate('/quotes');
  };

  const handleVisualize = () => {
    if (quoteData.id) {
      navigate(`/quotes/${quoteData.id}`);
    }
  };

  const handleSelectClient = (client: any) => {
    if (!client) return;

    // ✅ CRITIQUE: Capturer le clientId (obligatoire pour backend)
    updateField('clientId', client.id);
    updateField('clientName', client.fullName || client.companyName || '');
    updateField('clientCompany', client.companyName || '');
    updateField('clientEmail', client.email || '');
    updateField('clientPhone', client.phone || '');
    updateField('clientAddressLine1', client.addressLine1 || client.address || '');
    updateField('clientCity', client.city || '');
    updateField('clientZipCode', client.zipCode || client.postalCode || '');
    updateField('clientCountry', client.countryCode || client.country || 'FR');
  };

  // Convertir les données pour le Dashboard (format imbriqué attendu)
  const convertToQuoteDataForDashboard = (): any => ({
    number: quoteData.quoteNumber,
    issueDate: quoteData.date,
    validUntil: quoteData.validUntil,
    company: {
      legalName: finalCompany?.legalName || finalCompany?.tradeName || '',
      addressLine1: finalCompany?.addressLine1 || '',
      city: finalCompany?.city || '',
      zipCode: finalCompany?.zipCode || '',
      countryCode: finalCompany?.taxCountryCode || 'FR',
      phone: finalCompany?.phone || '',
      email: finalCompany?.email || '',
    },
    client: {
      fullName: quoteData.clientName || '',
      companyName: quoteData.clientCompany || '',
      addressLine1: quoteData.clientAddressLine1 || '',
      city: quoteData.clientCity || '',
      zipCode: quoteData.clientZipCode || '',
      countryCode: 'FR',
      phone: quoteData.clientPhone || '',
      email: quoteData.clientEmail || '',
    },
    items: quoteData.items,
    eventTitle: quoteData.title,  // ✅ Aligné sur Prisma
    internalNotes: quoteData.internalNotes,
    termsConditions: quoteData.termsConditions || '',
    discountType: quoteData.discountType,
    discountValue: quoteData.discountValue,
    discountAmount: quoteData.discountAmount,
  });

  return (
    <div className="min-h-screen bg-transparent">

      {/* Contenu principal - Style minimaliste */}
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-8">
          {/* Header ultra-minimaliste */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-12 pb-8 border-b border-white/30 gap-6">
            {/* Devis info LEFT */}
            <div className="flex-1">
              <h1 className="text-6xl md:text-7xl font-thin tracking-tighter text-gray-900 mb-4">
                {t('templates.quote.title')}
              </h1>
              <div className="text-sm font-mono text-gray-600">
                N° {quoteData.quoteNumber?.replace(/^DEVIS-/i, '') || quoteData.quoteNumber}
              </div>

              {/* Dates éditables */}
              <div className="mt-6 space-y-3 text-sm font-mono">
                <div className="flex gap-4 items-center">
                  <span className="text-gray-500 uppercase tracking-wider text-xs w-32">{t('templates.quote.date')}</span>
                  <input
                    type="date"
                    value={quoteData.date}
                    onChange={(e) => updateField('date', e.target.value)}
                    className="px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                  />
                </div>
                <div className="flex gap-4 items-center">
                  <span className="text-gray-500 uppercase tracking-wider text-xs w-32">{t('templates.common.validUntil')}</span>
                  <input
                    type="date"
                    value={quoteData.validUntil}
                    onChange={(e) => updateField('validUntil', e.target.value)}
                    className="px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                  />
                </div>
              </div>
            </div>

            {/* Company Info (Logo RIGHT) */}
            <div className="md:text-right flex-shrink-0">
              <CompanyInfoSection
                company={finalCompany}
                companyName={quoteData.companyName}
                companyAddress={quoteData.companyAddressLine1}
                companyCity={quoteData.companyCity}
                companyPostalCode={quoteData.companyZipCode}
                companyPhone={quoteData.companyPhone}
                companyEmail={quoteData.companyEmail}
                logoPosition="top-right"
              />
            </div>
          </div>

          {/* Section Client Éditable */}
          <div className="mb-12 max-w-md">
            <EditableClientSection
              data={quoteData}
              onUpdate={updateField}
              onSelectClient={handleSelectClient}
              accentColor="#000000"
            />
          </div>

          {/* Titre du document (éditable) - Style Corporate minimaliste */}
          <div className="mb-8">
            <input
              type="text"
              value={quoteData.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder={t('templates.quote.subjectPlaceholderAlt')}
              className="w-full px-0 py-2 text-xl font-thin tracking-wide bg-transparent border-0 border-b border-gray-300 focus:ring-0 focus:border-gray-900 transition-all duration-300 placeholder:text-gray-300"
              style={{ color: '#000000' }}
            />
          </div>

          {/* Tableau des items éditable - Style minimal */}
          <EditableItemsTable
            items={quoteData.items}
            onUpdateItem={updateItem}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onAddFromCatalog={() => setShowProductSelector(true)}
            tableStyle="minimal"
            accentColor="#000000"
            showVat={!isExempt}
            taxRates={taxRates}
            loadingTaxRates={loadingTaxRates}
            onAddSectionTitle={addSectionTitle}
            onAddComment={addComment}
            onAddSubtotal={addSubtotal}
            features={{
              showReference: companyFeatures?.referenceSKU ?? true,
              showUnit: companyFeatures?.unitOfMeasure ?? true,
              showDiscount: companyFeatures?.lineDiscount ?? true,
              showSections: companyFeatures?.sections ?? true,
            }}
          />

          {/* Section Remise éditable */}
          <div className="mt-8">
            <EditableDiscountSection
              data={quoteData}
              onUpdateType={updateDiscountType}
              onUpdateValue={updateDiscountValue}
              accentColor="#000000"
            />
          </div>

          {/* Section Totaux */}
          <TotalsSection
            subtotal={quoteData.subtotal}
            tax={quoteData.tax}
            total={quoteData.total}
            discount={quoteData.discountAmount}
            discountType={quoteData.discountType}
            discountValue={quoteData.discountValue}
            accentColor="#000000"
            showVatBreakdown={!isExempt}
          />

          {/* Section Notes éditable */}
          <div className="mt-12">
            <EditableNotesSection
              data={quoteData}
              onUpdate={updateField}
              accentColor="#000000"
            />
          </div>

          {/* ✅ AJOUTÉ: Champs étendus (workflow, signature, multi-devise) */}
          <div className="mt-12">
            <EditableQuoteExtendedFields
              data={quoteData}
              onUpdate={updateField}
              accentColor="#000000"
            />
          </div>

          {/* Footer légal - Style minimaliste */}
          <LegalFooter company={finalCompany} notes={quoteData.termsConditions} />
        </div>
      </div>

      {/* Dashboard Modal */}
      {showDashboard && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55] flex items-center justify-center p-2 md:p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 w-full md:max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-white/30 px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
              <h2 className="text-lg md:text-xl font-bold text-gray-800">
                {t('templates.common.dashboardTitle')}
              </h2>
              <button
                onClick={() => setShowDashboard(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl flex-shrink-0 ml-2 p-2 hover:bg-white/50 rounded-xl transition-all duration-300"
              >
                ×
              </button>
            </div>
            <div className="p-4 md:p-6">
              <Dashboard
                quoteData={convertToQuoteDataForDashboard()}
                onUpdateQuote={() => {}}
                onSave={saveQuote}
              />
            </div>
          </div>
        </div>
      )}

      {/* Product Selector Modal */}
      {showProductSelector && (
        <ProductSelector
          onSelectProduct={async (product: any) => {
            await addProductFromCatalog(product);
            setShowProductSelector(false);
          }}
          onClose={() => setShowProductSelector(false)}
        />
      )}

      {/* Boutons d'actions flottants */}
      <EditActionButtons
        onSave={saveQuote}
        onCancel={handleCancel}
        onShowDashboard={() => setShowDashboard(true)}
        onVisualize={handleVisualize}
        isSaving={isSaving}
        hasChanges={hasChanges}
        accentColor="#000000"
        documentType="quote"
      />
    </div>
  );
};
